import { createI18n } from "vue-i18n";
import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGE_CODES,
} from "./languages";
import { activeLocale, ensureAutoLocaleLoaded } from "./gt";

import de from "./locales/de.json";
import en from "./locales/en.json";
import es from "./locales/es.json";
import ru from "./locales/ru.json";
import uk from "./locales/uk.json";
import hu from "./locales/hu.json";
import tr from "./locales/tr.json";
import ro from "./locales/ro.json";
import fr from "./locales/fr.json";
import pl from "./locales/pl.json";
import cs from "./locales/cs.json";
import it from "./locales/it.json";
import hr from "./locales/hr.json";
import bs from "./locales/bs.json";

const LOCALE_STORAGE_KEY = "kars-locale";

// Resolve the initial locale: persisted choice → browser language → default.
// We only ever accept a locale we actually ship, so an exotic browser
// language gracefully falls back to German instead of showing raw keys.
function resolveInitialLocale(): string {
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && SUPPORTED_LANGUAGE_CODES.includes(stored)) {
      return stored;
    }
  } catch {
    // localStorage may be unavailable (private mode / SSR) — ignore.
  }

  const nav = (navigator.language || "").slice(0, 2).toLowerCase();
  if (SUPPORTED_LANGUAGE_CODES.includes(nav)) {
    return nav;
  }

  return DEFAULT_LANGUAGE;
}

const initialLocale = resolveInitialLocale();

const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: initialLocale,
  fallbackLocale: DEFAULT_LANGUAGE,
  messages: { de, en, es, ru, uk, hu, tr, ro, fr, pl, cs, it, hr, bs },
});

// Keep <html lang="..."> in sync for accessibility / SEO from the start.
document.documentElement.setAttribute("lang", initialLocale);

// Sync the German-source dictionary layer ($gt) with the initial choice and
// preload its dictionary so a persisted non-German language renders at once.
activeLocale.value = initialLocale;
ensureAutoLocaleLoaded(initialLocale);

/**
 * Change the active UI language and persist it for future sessions.
 */
export function setLanguage(code: string): void {
  if (!SUPPORTED_LANGUAGE_CODES.includes(code)) {
    return;
  }
  i18n.global.locale.value = code as typeof i18n.global.locale.value;
  document.documentElement.setAttribute("lang", code);
  // Switch the German-source dictionary layer ($gt) together with vue-i18n.
  activeLocale.value = code;
  ensureAutoLocaleLoaded(code);
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, code);
  } catch {
    // Persisting is best-effort; the in-memory switch still works.
  }
}

/** The currently active language code. */
export function getCurrentLanguage(): string {
  return i18n.global.locale.value;
}

export default i18n;
